import { useRef, useState } from "react";
import { VOTE_POINTS, pointsForRank, votesAllowed } from "../../convex/lib/game";
import { useVoteActions } from "../components/backend";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import type { Auth, ToastTone, VoteCandidate } from "../components/contracts";
import { Footer } from "../components/Footer";
import { NavBar } from "../components/NavBar";
import { Panel } from "../components/Panel";
import { cx, stagger, errorText } from "../components/util";

const TAP_SLOP_PX = 6;
const ORDINALS = ["1st", "2nd", "3rd"];

/** Pull filled ranks to the front, preserving order, padding with nulls. */
function compact(ranked: (string | null)[]): (string | null)[] {
  const filled = ranked.filter((id): id is string => id !== null);
  return Array.from({ length: ranked.length }, (_, i) => filled[i] ?? null);
}

export interface VoteProps {
  auth: Auth;
  round: number;
  /** Every player in the room, including you. */
  candidates: VoteCandidate[];
  onToast: (text: string, tone?: ToastTone) => void;
}

export function Vote({ auth, round, candidates, onToast }: VoteProps) {
  const { cast } = useVoteActions();
  const slots = votesAllowed(candidates.length);

  const [ranked, setRanked] = useState<(string | null)[]>(() => Array(slots).fill(null));
  const [dragId, setDragId] = useState<string | null>(null);
  const [hoverSlot, setHoverSlot] = useState<number | null>(null);
  const [sent, setSent] = useState(false);
  const origin = useRef({ x: 0, y: 0, moved: false });

  const votable = candidates.filter((c) => !c.isSelf);
  const filled = ranked.filter(Boolean).length;
  const canSubmit = filled > 0 && !sent;

  function assign(candidateId: string, slot: number) {
    setRanked((prev) => {
      const next = prev.map((id) => (id === candidateId ? null : id));
      next[slot] = candidateId;
      // Dropping into 3rd while 2nd is empty would leave a hole, and the
      // server reads the index as the rank. Squeeze it out.
      return compact(next);
    });
  }

  /** Tapping a candidate drops it in the first free slot. */
  function assignNext(candidateId: string) {
    const free = ranked.indexOf(null);
    if (free === -1) {
      onToast("All ranks are full. Tap a rank to clear it first.", "info");
      return;
    }
    assign(candidateId, free);
  }

  /**
   * Clearing shifts the ranks below it up. `castVotes` takes a plain array
   * where the index IS the rank, so the filled slots must always be a prefix —
   * a hole would silently promote whoever sat under it.
   */
  function clearSlot(slot: number) {
    setRanked((prev) => compact(prev.map((id, i) => (i === slot ? null : id))));
  }

  // --- Pointer drag: one code path for mouse, touch and pen ----------------

  function onPointerDown(e: React.PointerEvent<HTMLButtonElement>, candidateId: string) {
    origin.current = { x: e.clientX, y: e.clientY, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragId(candidateId);
  }

  function onPointerMove(e: React.PointerEvent<HTMLButtonElement>) {
    if (!dragId) return;
    const dx = e.clientX - origin.current.x;
    const dy = e.clientY - origin.current.y;
    if (!origin.current.moved && Math.hypot(dx, dy) > TAP_SLOP_PX) origin.current.moved = true;
    if (!origin.current.moved) return;

    const under = document.elementFromPoint(e.clientX, e.clientY);
    const slotEl = under?.closest<HTMLElement>("[data-rank-slot]");
    setHoverSlot(slotEl ? Number(slotEl.dataset.rankSlot) : null);
  }

  function onPointerUp(e: React.PointerEvent<HTMLButtonElement>, candidateId: string) {
    e.currentTarget.releasePointerCapture(e.pointerId);
    if (!origin.current.moved) {
      assignNext(candidateId); // it was a tap
    } else if (hoverSlot !== null) {
      assign(candidateId, hoverSlot);
    }
    setDragId(null);
    setHoverSlot(null);
  }

  async function send() {
    setSent(true);
    try {
      await cast(auth, round, ranked.filter((id): id is string => id !== null));
      onToast("Votes locked in.", "success");
    } catch (err) {
      setSent(false);
      onToast(errorText(err, "Couldn't submit your votes."), "error");
    }
  }

  const nameOf = (id: string | null) =>
    id ? (candidates.find((c) => c.playerId === id)?.name ?? "—") : null;

  return (
    <div className="bd-screen">
      <NavBar
        subnav={
          <>
            <span>Round {round}</span>
            <span aria-hidden="true">&middot;</span>
            <span>
              Rank {slots} of {votable.length}
            </span>
          </>
        }
      />

      <main className="shell bd-stack-lg">
        <Panel
          title="Your ranking"
          aside={
            <Badge tone={filled === slots ? "ready" : "amber"}>
              {filled}/{slots}
            </Badge>
          }
        >
          <div className="bd-rank-slots">
            {ranked.map((id, slot) => (
              <div
                key={slot}
                data-rank-slot={slot}
                className={cx(
                  "bd-rank-slot",
                  id && "is-filled",
                  hoverSlot === slot && "is-dropzone",
                )}
              >
                <span className="bd-rank-num" aria-hidden="true">
                  {slot + 1}
                </span>
                <span className="grow t-link truncate">
                  {nameOf(id) ?? (
                    <span className="muted">
                      {ORDINALS[slot] ?? `${slot + 1}th`} — drag or tap a name
                    </span>
                  )}
                </span>
                <Badge tone="carbon">{pointsForRank(slot)} pts</Badge>
                <Button
                  variant="secondary"
                  aria-label={`Clear ${ORDINALS[slot] ?? `rank ${slot + 1}`}`}
                  disabled={!id || sent}
                  onClick={() => clearSlot(slot)}
                >
                  <span aria-hidden="true">&times;</span>
                </Button>
              </div>
            ))}
          </div>
          <p className="t-micro muted">
            {VOTE_POINTS.join(" / ")} points. Anything you don&rsquo;t rank scores nothing.
          </p>
        </Panel>

        <Panel title="The dubs" animate={false}>
          <div className="bd-candidates">
            {candidates.map((candidate, i) => {
              const assigned = ranked.includes(candidate.playerId);
              return (
                <button
                  key={candidate.playerId}
                  type="button"
                  className={cx(
                    "bd-candidate",
                    "chip",
                    dragId === candidate.playerId && "is-dragging",
                    "bd-slide-in-left",
                  )}
                  style={stagger(i)}
                  data-self={candidate.isSelf}
                  data-assigned={assigned}
                  disabled={candidate.isSelf || sent}
                  aria-disabled={candidate.isSelf || undefined}
                  onPointerDown={(e) =>
                    !candidate.isSelf && !sent && onPointerDown(e, candidate.playerId)
                  }
                  onPointerMove={onPointerMove}
                  onPointerUp={(e) =>
                    !candidate.isSelf && !sent && onPointerUp(e, candidate.playerId)
                  }
                  onPointerCancel={() => {
                    setDragId(null);
                    setHoverSlot(null);
                  }}
                >
                  <span className="grow truncate">{candidate.name}</span>
                  {candidate.isSelf ? (
                    <Badge tone="carbon">That&rsquo;s you</Badge>
                  ) : assigned ? (
                    <Badge tone="ready">#{ranked.indexOf(candidate.playerId) + 1}</Badge>
                  ) : null}
                </button>
              );
            })}
          </div>
          <p className="t-micro muted">
            You can&rsquo;t vote for yourself. Nice try.
          </p>
        </Panel>

        <Button
          variant="submit"
          block
          beacon={canSubmit}
          disabled={!canSubmit}
          onClick={() => void send()}
        >
          {sent ? "Votes in — waiting for the room" : "Submit votes"}
        </Button>
      </main>

      <Footer />
    </div>
  );
}
